/**
 * SwaggerParser
 *
 * A class for parsing and processing Azure managed connector swagger specs.
 * Based on the LogicAppsUX SwaggerParser pattern.
 *
 * Key features:
 * - Dereferences $ref pointers in swagger specs
 * - Extracts input parameters with visibility filtering
 * - Sorts parameters by importance (required → important → default → advanced)
 */

import type {
  SwaggerSpec,
  SwaggerParameter,
  SwaggerOperation,
  SwaggerDefinition,
  SwaggerProperty,
  SwaggerVisibility,
  SwaggerPathItem,
  SwaggerSerializationOptions,
} from '@/types/swagger';
import type {
  ParameterInfo,
  DynamicValuesConfig,
  DynamicValueParameter,
  InputDependency,
  DynamicSchemaConfig,
  DynamicSchemaParameter,
  DynamicTreeConfig,
  DynamicTreePickerInfo,
  DynamicTreeParameter,
  DynamicTreeSettings,
  ParameterSerializationOptions,
  SerializationProperty,
} from '@/types/operationParameter';
import {
  ParameterType,
  ParameterVisibility,
  ParameterEditor,
  DynamicLoadStatus,
  InputDependencyType,
} from '@/types/operationParameter';
import type { SchemaObject } from '@/types/valueSegment';

// =============================================================================
// CONSTANTS
// =============================================================================

const ExtensionProperties = {
  Visibility: 'x-ms-visibility',
  Summary: 'x-ms-summary',
  Trigger: 'x-ms-trigger',
  DynamicValues: 'x-ms-dynamic-values',
  DynamicList: 'x-ms-dynamic-list',
  DynamicSchema: 'x-ms-dynamic-schema',
  DynamicTree: 'x-ms-dynamic-tree',
  InputDependencies: 'x-ms-input-dependencies',
  Editor: 'x-ms-editor',
  EditorOptions: 'x-ms-editor-options',
  Serialization: 'x-ms-serialization',
  UrlEncoding: 'x-ms-url-encoding',
  // Notification/webhook trigger extensions
  Notification: 'x-ms-notification',
  NotificationContent: 'x-ms-notification-content',
  NotificationUrl: 'x-ms-notification-url',
} as const;

const Visibility = {
  Advanced: 'advanced',
  Internal: 'internal',
  Important: 'important',
} as const;

const ReservedParameterNames = {
  ConnectionId: 'connectionId',
} as const;

// =============================================================================
// INTERFACES
// =============================================================================

export interface GetInputParametersOptions {
  excludeAdvancedParameters?: boolean;
  excludeInternalParameters?: boolean;
  /**
   * For notification triggers, include parameters from both fetch and subscribe operations.
   * When true, fetches parameters from:
   * - The trigger operation (fetch operation)
   * - The related notification operation (subscribe via x-ms-notification.operationId)
   *
   * Parameters are prefixed with their location for proper serialization:
   * - fetch.queries.{paramName} -> inputs.$.fetch.queries.{paramName}
   * - subscribe.body.{paramName} -> inputs.$.subscribe.body.{paramName}
   *
   * This follows Logic Apps UX pattern for ApiConnectionNotification triggers.
   */
  includeNotificationParameters?: boolean;
}

export interface GetOutputParametersOptions {
  excludeAdvancedOutputs?: boolean;
  excludeInternalOutputs?: boolean;
}

export interface ParsedOperation {
  operationId: string;
  path: string;
  method: string;
  operation: SwaggerOperation;
}

interface CyclicalRefMetadata {
  type: string;
}

export interface DereferencedSwagger extends SwaggerSpec {
  $refs?: Record<string, CyclicalRefMetadata>;
}

// =============================================================================
// PATH MATCHING UTILITIES
// =============================================================================

/**
 * Escape regex special characters in a string, except for .*
 * Based on LogicAppsUX escapeRegExSpecialCharacters
 */
function escapeRegExSpecialCharacters(pattern: string): string {
  return pattern.replace(/([+?^=!:${}()|[\]/\\])/g, '\\$1');
}

/**
 * Convert a swagger path to a regex pattern for matching.
 * Path parameters like {team-id} are converted to .* wildcards.
 * Based on LogicAppsUX getOperationIdFromSwagger pattern.
 */
function swaggerPathToRegex(swaggerPath: string): RegExp {
  // Replace all {param} patterns with .*
  let pattern = swaggerPath.replace(/{[^}]+}/g, '.*');

  // Escape regex special characters (except . and * which we already handled)
  pattern = escapeRegExSpecialCharacters(pattern);

  // Replace the escaped \.\* back to .*
  pattern = pattern.replace(/\\\.\\\*/g, '.*');

  // Add start and end markers
  pattern = `^${pattern}$`;

  return new RegExp(pattern, 'i');
}

/**
 * Remove the {connectionId} prefix from swagger paths.
 * Managed connector swaggers have paths like /{connectionId}/beta/teams/{team-id}
 * but runtime paths don't include this prefix.
 * Based on LogicAppsUX removeConnectionPrefix.
 */
function removeConnectionPrefix(pathTemplate: string): string {
  const pathPrefix = '{connectionId}';
  let updatedTemplate = pathTemplate;
  updatedTemplate = updatedTemplate.replace(pathPrefix, '');
  updatedTemplate = updatedTemplate.replace('//', '/');
  return updatedTemplate;
}

/**
 * Normalize Logic Apps expression paths for swagger matching.
 * Runtime paths may contain expressions like @{encodeURIComponent(...)} which
 * are dynamically resolved. We replace these with a placeholder that matches
 * swagger path parameter patterns.
 *
 * Examples:
 * - /@{encodeURIComponent(encodeURIComponent('v2/Mail'))}/v2/Mail -> /__EXPR__/v2/Mail
 * - /folders/@{variables('folderId')}/messages -> /folders/__EXPR__/messages
 */
function normalizeExpressionPath(inputPath: string): string {
  // Replace @{...} and ${...} expressions with a placeholder
  // Match @{ or ${ followed by any characters (non-greedy) until }
  return inputPath.replace(/[@$]\{[^}]+\}/g, '__EXPR__');
}

/**
 * Find the best matching operation from swagger by path and method.
 * Uses regex matching like LogicAppsUX - path parameters match any value.
 * @param inputPath - The runtime path (may have template expressions replaced with {param})
 * @param method - HTTP method
 * @param paths - Swagger paths object
 * @returns The matching path and operation, or null
 */
function findOperationByPathAndMethod(
  inputPath: string,
  method: string,
  paths: Record<string, SwaggerPathItem>
): { swaggerPath: string; operation: SwaggerOperation } | null {
  const normalizedMethod = method.toLowerCase() as keyof SwaggerPathItem;

  // Build list of candidate operations with their regex patterns
  const candidates: Array<{
    swaggerPath: string;
    normalizedPath: string;
    operation: SwaggerOperation;
    regex: RegExp;
  }> = [];

  for (const [swaggerPath, pathItem] of Object.entries(paths)) {
    const operation = pathItem[normalizedMethod];
    if (operation) {
      // Remove {connectionId} prefix from swagger path (following LogicAppsUX)
      const normalizedPath = removeConnectionPrefix(swaggerPath);
      const regex = swaggerPathToRegex(normalizedPath);
      candidates.push({
        swaggerPath,
        normalizedPath,
        operation,
        regex,
      });
    }
  }

  // Sort by path length (longer = more specific = higher priority)
  candidates.sort((a, b) => b.normalizedPath.length - a.normalizedPath.length);

  // Ignore newline characters in input path (following LogicAppsUX)
  let cleanedInputPath = inputPath.replace(/[\r\n]/g, '');

  // Normalize Logic Apps expressions (e.g., @{encodeURIComponent(...)}) to placeholders
  // This allows matching against swagger path parameters
  cleanedInputPath = normalizeExpressionPath(cleanedInputPath);

  // Find first matching operation
  for (const candidate of candidates) {
    if (candidate.regex.test(cleanedInputPath)) {
      return {
        swaggerPath: candidate.swaggerPath,
        operation: candidate.operation,
      };
    }
  }

  return null;
}

// =============================================================================
// SWAGGER PARSER CLASS
// =============================================================================

export class SwaggerParser {
  private api: DereferencedSwagger;

  constructor(swagger: SwaggerSpec) {
    this.api = swagger as DereferencedSwagger;
  }

  /**
   * Parse and dereference a swagger document.
   * Resolves all local $ref pointers, detecting and handling cyclical references.
   */
  static async parse(swagger: SwaggerSpec): Promise<DereferencedSwagger> {
    return dereferenceSwagger(swagger);
  }

  /**
   * Get all input parameters for an operation.
   * Parameters are filtered by visibility and sorted by importance.
   */
  getInputParameters(
    operationId: string,
    options: GetInputParametersOptions = {}
  ): ParameterInfo[] {
    const {
      excludeAdvancedParameters = false,
      excludeInternalParameters = true,
      includeNotificationParameters = false,
    } = options;

    const operation = this.getOperationByOperationId(operationId);
    if (!operation) {
      return [];
    }

    // For notification triggers with includeNotificationParameters, get params from both operations
    if (includeNotificationParameters) {
      const notificationParams = this.getNotificationParameters(
        operationId,
        excludeAdvancedParameters,
        excludeInternalParameters
      );
      if (notificationParams.length > 0) {
        return notificationParams;
      }
    }

    const parameters = operation.operation.parameters ?? [];

    // Filter parameters by visibility
    const filteredParams = parameters
      .filter((param) => {
        if (isReservedParameter(param)) {
          return false;
        }
        const visibility = param[ExtensionProperties.Visibility];
        if (excludeInternalParameters && visibility === Visibility.Internal) {
          // Carve-out: keep internal params that declare a `default` so the
          // value still reaches the runtime (e.g. required Accept headers).
          // They're flagged hideInUI in convertToParameterInfo.
          if (param.default === undefined) return false;
        }
        if (excludeAdvancedParameters && visibility === Visibility.Advanced) {
          return false;
        }
        return true;
      });

    // Sort parameters: required → important → default → advanced
    const sortedParams = sortParametersByImportance(filteredParams);

    // Convert to ParameterInfo
    return sortedParams.flatMap((param) => this.processParameter(param));
  }

  /**
   * Get parameters for a notification trigger, including both fetch and subscribe operations.
   *
   * Notification triggers have two parts:
   * - Fetch operation: The trigger operation that retrieves data (has query params like folder, importance)
   * - Subscribe operation: Sets up the webhook subscription (has body params like NotificationUrl)
   *
   * Parameters are given key prefixes following Logic Apps pattern:
   * - Query params from fetch: `fetch.queries.{paramName}` (serializes to inputs.fetch.queries.{name})
   * - Body params from subscribe: Not included (NotificationUrl is auto-generated)
   *
   * For user-facing display, the fetch operation's query parameters are the ones
   * that users configure (e.g., folder to monitor, importance filter).
   */
  private getNotificationParameters(
    operationId: string,
    excludeAdvanced: boolean,
    excludeInternal: boolean
  ): ParameterInfo[] {
    const fetchOperation = this.getOperationByOperationId(operationId);
    if (!fetchOperation) {
      return [];
    }

    // Check if this is a notification trigger (has x-ms-notification extension)
    const subscribeOperationId = this.getRelatedNotificationOperationId(operationId);
    if (!subscribeOperationId) {
      // Not a notification trigger
      return [];
    }

    const result: ParameterInfo[] = [];

    // Get parameters from the fetch operation (trigger operation)
    // These are the user-configurable parameters (like folder, importance, etc.)
    const fetchParams = fetchOperation.operation.parameters ?? [];
    for (const param of fetchParams) {
      // Skip reserved and visibility-filtered params
      if (isReservedParameter(param)) continue;
      const visibility = param[ExtensionProperties.Visibility];
      if (excludeInternal && visibility === Visibility.Internal) continue;
      if (excludeAdvanced && visibility === Visibility.Advanced) continue;

      // Process the parameter
      const paramInfos = this.processParameter(param);
      for (const paramInfo of paramInfos) {
        // Add key prefix for notification trigger structure
        // Query params go to fetch.queries, which serializes to inputs.fetch.queries
        if (param.in === 'query') {
          paramInfo.parameterKey = `fetch.queries.${paramInfo.parameterKey}`;
          // Store location for serialization
          paramInfo.info = {
            ...paramInfo.info,
            in: 'query',
            notificationLocation: 'fetch',
          };
        } else if (param.in === 'path') {
          // Path params go to fetch.path.template
          paramInfo.parameterKey = `fetch.path.${paramInfo.parameterKey}`;
          paramInfo.info = {
            ...paramInfo.info,
            in: 'path',
            notificationLocation: 'fetch',
          };
        }
        result.push(paramInfo);
      }
    }

    // Get parameters from subscribe operation (for completeness, but most are internal)
    // The NotificationUrl body param is always auto-generated, so we skip body params
    const subscribeOperation = this.getOperationByOperationId(subscribeOperationId);
    if (subscribeOperation) {
      const subscribeParams = subscribeOperation.operation.parameters ?? [];
      for (const param of subscribeParams) {
        // Skip reserved params
        if (isReservedParameter(param)) continue;
        const visibility = param[ExtensionProperties.Visibility];
        if (excludeInternal && visibility === Visibility.Internal) continue;
        if (excludeAdvanced && visibility === Visibility.Advanced) continue;

        // Skip body params (NotificationUrl is auto-generated)
        if (param.in === 'body') continue;

        // Check if this param already exists from fetch (they often share query params)
        const alreadyAdded = result.some((p) =>
          p.parameterName === param.name && p.info?.notificationLocation === 'fetch'
        );
        if (alreadyAdded) continue;

        // Process the parameter
        const paramInfos = this.processParameter(param);
        for (const paramInfo of paramInfos) {
          // Add key prefix for subscribe location
          if (param.in === 'query') {
            paramInfo.parameterKey = `subscribe.queries.${paramInfo.parameterKey}`;
            paramInfo.info = {
              ...paramInfo.info,
              in: 'query',
              notificationLocation: 'subscribe',
            };
          }
          result.push(paramInfo);
        }
      }
    }

    // Sort by importance
    return sortParameterInfoByImportance(result);
  }

  /**
   * Get all output parameters for an operation.
   * Extracts parameters from the operation's response schema.
   */
  getOutputParameters(
    operationId: string,
    options: GetOutputParametersOptions = {}
  ): ParameterInfo[] {
    const { excludeAdvancedOutputs = false, excludeInternalOutputs = true } = options;

    const operation = this.getOperationByOperationId(operationId);
    if (!operation) {
      return [];
    }

    const responses = operation.operation.responses;
    const response = this.getSuccessResponse(responses);

    if (!response?.schema) {
      return [];
    }

    // Resolve the schema if it's a $ref
    const resolvedSchema = this.resolveSchema(response.schema);
    if (!resolvedSchema) {
      return [];
    }

    // Extract parameters from the response schema
    const parameters: ParameterInfo[] = [];

    if (resolvedSchema.properties) {
      const requiredProps = resolvedSchema.required ?? [];

      for (const [propName, propSchema] of Object.entries(resolvedSchema.properties)) {
        // Resolve $ref for each property if needed
        let resolvedPropSchema = propSchema;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ('$ref' in propSchema && typeof (propSchema as any).$ref === 'string') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const refResolved = this.resolveRef((propSchema as any).$ref);
          if (refResolved) {
            resolvedPropSchema = refResolved as SwaggerProperty;
          }
        }
        const paramInfo = this.propertyToParameterInfo(
          propName,
          propName,
          resolvedPropSchema,
          requiredProps.includes(propName)
        );
        if (paramInfo) {
          // Apply visibility filters
          if (excludeInternalOutputs && paramInfo.visibility === ParameterVisibility.Internal) {
            continue;
          }
          if (excludeAdvancedOutputs && paramInfo.visibility === ParameterVisibility.Advanced) {
            continue;
          }
          // Mark as output parameter
          paramInfo.info = { ...paramInfo.info, in: 'body' };
          parameters.push(paramInfo);
        }
      }
    } else if (resolvedSchema.type) {
      // Simple type response (not an object with properties)
      const paramInfo = this.schemaToParameterInfo('body', resolvedSchema);
      if (paramInfo) {
        paramInfo.info = { ...paramInfo.info, in: 'body' };
        parameters.push(paramInfo);
      }
    }

    return sortParameterInfoByImportance(parameters);
  }

  /**
   * Get the success response from responses object.
   * Prefers 200, then 201, then default.
   */
  private getSuccessResponse(responses: Record<string, { description: string; schema?: unknown }>): { description: string; schema?: unknown } | null {
    if (responses['200']) return responses['200'];
    if (responses['201']) return responses['201'];
    if (responses['202']) return responses['202'];
    if (responses['default']) return responses['default'];

    // If only one response, return it
    const responseCodes = Object.keys(responses);
    if (responseCodes.length === 1 && responseCodes[0]) {
      const response = responses[responseCodes[0]];
      if (response) return response;
    }

    return null;
  }

  /**
   * Convert a schema to ParameterInfo (for simple/non-object type responses).
   * Uses resolveSchemaDeep to fully expand the schema (including $ref items).
   */
  private schemaToParameterInfo(name: string, schema: SwaggerDefinition): ParameterInfo | null {
    const paramType = swaggerTypeToParameterType(schema.type);
    const visibility = swaggerVisibilityToParameterVisibility(schema[ExtensionProperties.Visibility]);
    const schemaObj = this.resolveSchemaDeep(schema as SwaggerProperty);

    return {
      id: `param_${name}`,
      parameterKey: name,
      parameterName: name,
      label: formatLabel(name),
      type: paramType,
      required: false,
      editor: ParameterEditor.String,
      visibility,
      schema: schemaObj,
      value: [],
      info: {},
      dynamicData: {
        status: DynamicLoadStatus.NotStarted,
      },
      showTokens: true,
      showErrors: true,
    };
  }

  /**
   * Get an operation by its operationId.
   * Supports exact match, and fallback matching by family name (strips _V2, _V3 etc suffixes).
   */
  getOperationByOperationId(operationId: string): ParsedOperation | null {
    const normalizedOperationId = operationId.toLowerCase();
    const methods: Array<keyof SwaggerPathItem> = ['get', 'post', 'put', 'patch', 'delete', 'options', 'head'];

    // First try exact match
    for (const [path, pathItem] of Object.entries(this.api.paths)) {
      for (const method of methods) {
        const operation = pathItem[method];
        if (operation && operation.operationId.toLowerCase() === normalizedOperationId) {
          return {
            operationId: operation.operationId,
            path,
            method,
            operation,
          };
        }
      }
    }

    // Fallback: try matching by family name (strip version suffix like _V2, _V3)
    // Logic Apps often has versioned operationIds (OnUpdatedFiles_V2) but swagger may only have base name
    const versionPattern = /_v\d+$/i;
    if (versionPattern.test(operationId)) {
      const baseOperationId = operationId.replace(versionPattern, '').toLowerCase();

      for (const [path, pathItem] of Object.entries(this.api.paths)) {
        for (const method of methods) {
          const operation = pathItem[method];
          if (operation && operation.operationId.toLowerCase() === baseOperationId) {
            return {
              operationId: operation.operationId,
              path,
              method,
              operation,
            };
          }
        }
      }
    }

    // Fallback: try matching by x-ms-api-annotation family name
    // Some operations have family metadata that maps versions to the same family
    for (const [path, pathItem] of Object.entries(this.api.paths)) {
      for (const method of methods) {
        const operation = pathItem[method];
        if (operation) {
          // Cast to access extension property that may not be in the strict type
          const opWithExtensions = operation as SwaggerOperation & { 'x-ms-api-annotation'?: { family?: string } };
          const apiAnnotation = opWithExtensions['x-ms-api-annotation'];
          if (apiAnnotation?.family?.toLowerCase() === normalizedOperationId.replace(/_v\d+$/i, '')) {
            return {
              operationId: operation.operationId,
              path,
              method,
              operation,
            };
          }
        }
      }
    }

    return null;
  }

  /**
   * Resolve the runtime MCP server path for a managed MCP connector's
   * InvokeMCP operation. The managed list-tools probe and the workflow
   * runtime both expect the swagger operation path with the
   * `{connectionId}` prefix removed (mirrors LogicAppsUX `getMcpServerPath`).
   *
   * @param operationId - The InvokeMCP operation id.
   * @returns The connection-prefix-stripped path, or null if not found.
   */
  getMcpServerPath(operationId: string): string | null {
    const operation = this.getOperationByOperationId(operationId);
    if (!operation) return null;
    return removeConnectionPrefix(operation.path);
  }

  /**
   * Resolve an operationId from a trigger's fetch path template.
   *
   * Notification triggers store the path without the {connectionId} prefix:
   * - Trigger path: `/v2/Mail/OnNewEmail`
   * - Swagger path: `/{connectionId}/v2/Mail/OnNewEmail`
   *
   * This method handles the prefix mismatch and returns the operationId.
   *
   * @param fetchPath - The path from fetch.pathTemplate.template
   * @param method - The HTTP method (default: 'get' for triggers)
   * @returns The operationId or null if not found
   */
  resolveOperationIdFromFetchPath(fetchPath: string, method: string = 'get'): string | null {

    // Try direct match first
    let operation = this.getOperationByPathAndMethod(fetchPath, method);
    if (operation) {
      return operation.operationId;
    }

    // Try with {connectionId} prefix (common pattern for managed connectors)
    const pathWithPrefix = `/{connectionId}${fetchPath}`;
    operation = this.getOperationByPathAndMethod(pathWithPrefix, method);
    if (operation) {
      return operation.operationId;
    }

    // Try matching by path suffix (in case of different prefix patterns)
    const normalizedFetchPath = fetchPath.toLowerCase();
    for (const [swaggerPath, pathItem] of Object.entries(this.api.paths)) {
      // Check if swagger path ends with the fetch path
      const normalizedSwaggerPath = swaggerPath.toLowerCase();
      if (normalizedSwaggerPath.endsWith(normalizedFetchPath)) {
        const normalizedMethod = method.toLowerCase() as keyof SwaggerPathItem;
        const op = pathItem[normalizedMethod];
        if (op) {
          return op.operationId;
        }
      }
    }
    
    return null;
  }

  /**
   * Get an operation by its path and HTTP method.
   * Useful when operationId is not available (legacy tools).
   * Uses regex-based path matching following LogicAppsUX pattern.
   */
  getOperationByPathAndMethod(path: string, method: string): ParsedOperation | null {
    const result = findOperationByPathAndMethod(path, method, this.api.paths);

    if (result) {
      return {
        operationId: result.operation.operationId,
        path: result.swaggerPath,
        method: method.toLowerCase(),
        operation: result.operation,
      };
    }

    return null;
  }

  /**
   * Build a parameter name mapping for an operation.
   * Maps various forms of parameter references to their canonical names.
   *
   * This solves the mismatch between x-ms-dynamic-schema parameter references
   * (e.g., "PostAs") and the actual parameter names in parameterValues (e.g., "poster").
   *
   * Mapping strategies:
   * - Direct name: "poster" → "poster"
   * - Case variations: "Poster", "POSTER" → "poster"
   * - x-ms-summary variations: "Post as" → "poster" (camelCase: "postas" → "poster")
   *
   * @param operationId - The operation to build mappings for
   * @returns Map from any reference name format (lowercase) to canonical parameter name
   */
  buildParameterNameMapping(operationId: string): Map<string, string> {
    const operation = this.getOperationByOperationId(operationId);
    const mapping = new Map<string, string>();

    if (!operation?.operation.parameters) {
      return mapping;
    }

    this.addParametersToMapping(operation.operation.parameters, mapping);
    return mapping;
  }

  /**
   * Build a global parameter name mapping from ALL operations in the swagger.
   * Use this when the specific operation context is not available.
   *
   * @returns Map from any reference name format (lowercase) to canonical parameter name
   */
  buildGlobalParameterNameMapping(): Map<string, string> {
    const mapping = new Map<string, string>();
    const operations = this.getOperations({ excludeInternalOperations: false });

    for (const op of operations) {
      if (op.operation.parameters) {
        this.addParametersToMapping(op.operation.parameters, mapping);
      }
    }

    return mapping;
  }

  /**
   * Helper to add parameters to a mapping.
   * Extracts canonical name and x-ms-summary variations.
   */
  private addParametersToMapping(
    parameters: SwaggerParameter[],
    mapping: Map<string, string>
  ): void {
    for (const param of parameters) {
      const canonicalName = param.name;
      const lowerName = canonicalName.toLowerCase();

      // 1. Direct lowercase mapping
      mapping.set(lowerName, canonicalName);

      // 2. x-ms-summary variations (handles "Post as" → "poster" mapping)
      const summary = param[ExtensionProperties.Summary];
      if (summary && typeof summary === 'string') {
        // camelCase version: "Post as" → "postas"
        const camelCase = summary.replace(/\s+/g, '').toLowerCase();
        if (camelCase !== lowerName) {
          mapping.set(camelCase, canonicalName);
        }

        // PascalCase version: "Post as" → "PostAs" → "postas"
        const pascalCase = summary
          .split(/\s+/)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join('')
          .toLowerCase();
        if (pascalCase !== lowerName && pascalCase !== camelCase) {
          mapping.set(pascalCase, canonicalName);
        }
      }
    }
  }

  /**
   * Get all operations from the swagger spec.
   */
  getOperations(options: { excludeInternalOperations?: boolean } = {}): ParsedOperation[] {
    const { excludeInternalOperations = true } = options;
    const operations: ParsedOperation[] = [];

    for (const [path, pathItem] of Object.entries(this.api.paths)) {
      const methods: Array<keyof SwaggerPathItem> = ['get', 'post', 'put', 'patch', 'delete', 'options', 'head'];

      for (const method of methods) {
        const operation = pathItem[method];
        if (operation) {
          const visibility = operation[ExtensionProperties.Visibility];
          if (excludeInternalOperations && visibility === Visibility.Internal) {
            continue;
          }
          operations.push({
            operationId: operation.operationId,
            path,
            method,
            operation,
          });
        }
      }
    }

    return operations;
  }

  /**
   * Check if an operation is a trigger.
   */
  isTriggerOperation(operationId: string): boolean {
    const operation = this.getOperationByOperationId(operationId);
    return !!operation?.operation[ExtensionProperties.Trigger];
  }

  /**
   * Check if an operation is a notification-based trigger (subscription pattern).
   * Notification triggers use x-ms-notification extension with a related operationId
   * for the "fetch" operation that retrieves data when notified.
   *
   * Example: Office365 "When a new email arrives" trigger
   * - Subscribe operation sets up webhook
   * - Fetch operation (via x-ms-notification.operationId) retrieves the email data
   *
   * @returns The notification operationId if this is a notification trigger, undefined otherwise
   */
  getRelatedNotificationOperationId(operationId: string): string | undefined {
    const operation = this.getOperationByOperationId(operationId);
    if (!operation) {
      return undefined;
    }

    // Check for x-ms-notification extension on the operation
    const notification = operation.operation[ExtensionProperties.Notification] as
      | { operationId?: string }
      | undefined;

    return notification?.operationId;
  }

  /**
   * Check if an operation is a webhook trigger (callback pattern).
   * Webhook triggers use x-ms-notification-content to define the schema
   * of the callback payload that will be pushed to the workflow.
   *
   * Unlike notification triggers which poll/fetch data, webhook triggers
   * receive data directly in the callback.
   *
   * @returns true if this operation has notification content (is a webhook trigger)
   */
  operationIsWebhook(operationId: string): boolean {
    const operation = this.getOperationByOperationId(operationId);
    if (!operation) {
      return false;
    }

    // Check for x-ms-notification-content extension on the operation
    return !!operation.operation[ExtensionProperties.NotificationContent];
  }

  /**
   * Determine the operation type for a trigger based on swagger extensions.
   *
   * Returns:
   * - 'ApiConnectionNotification' if the trigger uses x-ms-notification (subscription + fetch pattern)
   * - 'ApiConnectionWebhook' if the trigger uses x-ms-notification-content (callback pattern)
   * - 'ApiConnection' for polling triggers (default)
   * - undefined if not a trigger or operation not found
   */
  getTriggerOperationType(operationId: string): 'ApiConnection' | 'ApiConnectionNotification' | 'ApiConnectionWebhook' | undefined {
    const operation = this.getOperationByOperationId(operationId);
    if (!operation) {
      return undefined;
    }

    // Only applies to triggers
    if (!operation.operation[ExtensionProperties.Trigger]) {
      return undefined;
    }

    // Check for notification pattern first (subscription-based like Office365 email)
    if (this.getRelatedNotificationOperationId(operationId)) {
      return 'ApiConnectionNotification';
    }

    // Check for webhook pattern (callback-based)
    if (this.operationIsWebhook(operationId)) {
      return 'ApiConnectionWebhook';
    }

    // Default to polling trigger
    return 'ApiConnection';
  }

  /**
   * Build the inputs structure for an ApiConnectionNotification trigger.
   *
   * Notification triggers have two operations:
   * - fetch: The trigger operation that retrieves data when notified
   * - subscribe: The related notification operation that sets up the webhook subscription
   *
   * @returns The inputs structure with fetch and subscribe sections, or null if not a notification trigger
   */
  buildNotificationTriggerInputs(operationId: string): {
    fetch: { method: string; pathTemplate: { template: string }; queries?: Record<string, unknown> };
    subscribe: { method: string; pathTemplate: { template: string }; body: Record<string, unknown>; queries?: Record<string, unknown> };
  } | null {
    // Get the fetch operation (the trigger operation itself)
    const fetchOperation = this.getOperationByOperationId(operationId);
    if (!fetchOperation) {
      return null;
    }

    // Get the related notification operation ID (subscribe operation)
    const subscribeOperationId = this.getRelatedNotificationOperationId(operationId);
    if (!subscribeOperationId) {
      return null;
    }

    // Get the subscribe operation
    const subscribeOperation = this.getOperationByOperationId(subscribeOperationId);
    if (!subscribeOperation) {
      return null;
    }

    // Remove {connectionId} prefix from paths
    const removeConnectionPrefix = (path: string): string => {
      return path.replace(/^\/{connectionId}/, '').replace(/^\/\//, '/');
    };

    const fetchPath = removeConnectionPrefix(fetchOperation.path);
    const subscribePath = removeConnectionPrefix(subscribeOperation.path);

    // Build fetch section
    const fetchInputs: { method: string; pathTemplate: { template: string }; queries?: Record<string, unknown> } = {
      method: fetchOperation.method,
      pathTemplate: {
        template: fetchPath,
      },
    };

    // Add query parameters from fetch operation (directly from swagger)
    const fetchSwaggerParams = fetchOperation.operation.parameters ?? [];
    const fetchQueries: Record<string, unknown> = {};
    for (const param of fetchSwaggerParams) {
      if (param.in === 'query' && param.default !== undefined) {
        fetchQueries[param.name] = param.default;
      }
    }
    if (Object.keys(fetchQueries).length > 0) {
      fetchInputs.queries = fetchQueries;
    }

    // Build subscribe section
    const subscribeInputs: { method: string; pathTemplate: { template: string }; body: Record<string, unknown>; queries?: Record<string, unknown> } = {
      method: subscribeOperation.method,
      pathTemplate: {
        template: subscribePath,
      },
      body: {
        NotificationUrl: '${listCallbackUrl()}',
      },
    };

    // Add query parameters from subscribe operation (directly from swagger)
    const subscribeSwaggerParams = subscribeOperation.operation.parameters ?? [];
    const subscribeQueries: Record<string, unknown> = {};
    for (const param of subscribeSwaggerParams) {
      if (param.in === 'query' && param.default !== undefined) {
        subscribeQueries[param.name] = param.default;
      }
    }
    // Also include fetch queries in subscribe (they share common parameters)
    if (Object.keys(fetchQueries).length > 0 || Object.keys(subscribeQueries).length > 0) {
      subscribeInputs.queries = { ...fetchQueries, ...subscribeQueries };
    }

    return {
      fetch: fetchInputs,
      subscribe: subscribeInputs,
    };
  }

  /**
   * Get the subscribe operation path for a notification trigger.
   * @returns The subscribe operation path, or undefined if not a notification trigger
   */
  getSubscribeOperationPath(operationId: string): string | undefined {
    const subscribeOperationId = this.getRelatedNotificationOperationId(operationId);
    if (!subscribeOperationId) {
      return undefined;
    }

    const subscribeOperation = this.getOperationByOperationId(subscribeOperationId);
    if (!subscribeOperation) {
      return undefined;
    }

    // Remove {connectionId} prefix
    return subscribeOperation.path.replace(/^\/{connectionId}/, '').replace(/^\/\//, '/');
  }

  /**
   * Process a swagger parameter into one or more ParameterInfo objects.
   * Body parameters with object schemas are flattened into multiple parameters.
   */
  private processParameter(param: SwaggerParameter): ParameterInfo[] {
    if (param.in === 'body' && param.schema) {
      return this.processBodyParameter(param);
    }
    return [this.convertToParameterInfo(param)];
  }

  /**
   * Process a body parameter, flattening object schemas into individual parameters.
   */
  private processBodyParameter(param: SwaggerParameter): ParameterInfo[] {
    const schema = param.schema;
    if (!schema) {
      return [];
    }

    const hasDynamicSchema = !!(param[ExtensionProperties.DynamicSchema]);
    const schemaHasDynamicSchema = !!((schema as Record<string, unknown>)[ExtensionProperties.DynamicSchema]);

    // Check if body parameter has x-ms-dynamic-schema - if so, return it as-is
    // The schema will be fetched dynamically and converted to parameters later
    if (hasDynamicSchema || schemaHasDynamicSchema) {
      return [this.convertToParameterInfo(param)];
    }

    // Resolve $ref if present
    const resolvedSchema = this.resolveSchema(schema);
    if (!resolvedSchema) {
      return [];
    }

    // x-ms-dynamic-schema may live on the resolved $ref target (e.g. SharePoint
    // PostItem / PatchItem reference `#/definitions/Item`, which carries the
    // extension on the definition itself). Treat it like an inline dynamic-schema
    // body so the column editors get generated from the dynamicInvoke response.
    if ((resolvedSchema as Record<string, unknown>)[ExtensionProperties.DynamicSchema]) {
      return [this.convertToParameterInfo(param)];
    }

    // If the body has properties, flatten them
    if (resolvedSchema.properties) {
      const parameters: ParameterInfo[] = [];
      const requiredProps = resolvedSchema.required ?? [];

      for (const [propName, propSchema] of Object.entries(resolvedSchema.properties)) {
        const flattened = this.flattenBodyProperty(
          '',
          propName,
          propSchema,
          requiredProps.includes(propName),
          undefined,
        );
        parameters.push(...flattened);
      }

      // Sort the flattened parameters by importance
      return sortParameterInfoByImportance(parameters);
    }

    // No properties to flatten: this param IS the body. Mark `isBodyRoot`
    // so the serializer writes its value directly to `inputs.body` (parity
    // with LogicAppsUX's `body.$` root parameter in `constructInputValues`).
    const rootBody = this.convertToParameterInfo(param);
    rootBody.info = { ...rootBody.info, isBodyRoot: true };
    return [rootBody];
  }

  /**
   * Recursively flatten a body property into one or more ParameterInfo entries.
   *
   * Mirrors the manifest path's behavior in
   * `operationManifestService.parseSchemaToParameters`: nested objects with
   * properties are decomposed into individual editors so authors fill them out
   * field-by-field instead of editing a single dictionary blob (e.g. Outlook
   * Approval `Message` → `Message.To` / `Message.Subject` / ...).
   *
   * Do NOT flatten when:
   *   - the property has an explicit `x-ms-editor` (the author chose a composite editor),
   *   - it's a true open-ended map (`additionalProperties: true | { ... }`),
   *   - it's a dynamic-schema container (handled later by useDynamicSchema),
   *   - it has dynamic-values / dynamic-tree (the editor is bound to those).
   *
   * Children inherit `parentProperty` so the renderer wraps them in a single
   * collapsible group keyed off the parent name.
   */
  private flattenBodyProperty(
    parentKey: string,
    propName: string,
    prop: SwaggerProperty,
    required: boolean,
    parentGroup: { key: string; name: string; label: string } | undefined,
  ): ParameterInfo[] {
    // Resolve $ref defensively — the parser pre-dereferences, but inline $refs may remain.
    const resolved = ('$ref' in prop && typeof (prop as { $ref?: string }).$ref === 'string')
      ? (this.resolveRef((prop as { $ref: string }).$ref) as SwaggerProperty | null) ?? prop
      : prop;

    // Skip internal properties before flattening children, except for two
    // carve-outs that must reach the body serializer: x-ms-notification-url
    // (callback URL holder) and any prop with a `default` value. Surviving
    // internal leaves are flagged hideInUI below.
    const isInternal = resolved[ExtensionProperties.Visibility] === Visibility.Internal;
    const isNotificationUrlProp = !!resolved[ExtensionProperties.NotificationUrl];
    const hasDefault = resolved.default !== undefined;
    if (isInternal && !isNotificationUrlProp && !hasDefault) {
      return [];
    }

    const fullKey = parentKey ? `${parentKey}.${propName}` : propName;

    // Treat anything with `properties` as an object schema even when `type` is
    // omitted \u2014 Swagger / OpenAPI schemas frequently leave `type` off when
    // `properties` is present, and skipping those would leave nested fields
    // collapsed into a single dictionary editor.
    const isObject =
      !!resolved.properties &&
      (resolved.type === 'object' || resolved.type == null);
    const hasExplicitEditor = (resolved as Record<string, unknown>)[ExtensionProperties.Editor] !== undefined;
    const hasDynamicSchemaExt = !!(resolved as Record<string, unknown>)[ExtensionProperties.DynamicSchema];
    const hasDynamicValuesExt =
      !!(resolved as Record<string, unknown>)[ExtensionProperties.DynamicValues] ||
      !!(resolved as Record<string, unknown>)[ExtensionProperties.DynamicList] ||
      !!(resolved as Record<string, unknown>)[ExtensionProperties.DynamicTree];
    const additionalProps = (resolved as Record<string, unknown>)['additionalProperties'];
    const isOpenEndedMap =
      additionalProps === true ||
      (typeof additionalProps === 'object' && additionalProps !== null);

    const shouldFlatten =
      isObject &&
      !hasExplicitEditor &&
      !hasDynamicSchemaExt &&
      !hasDynamicValuesExt &&
      !isOpenEndedMap;

    if (shouldFlatten) {
      const parameters: ParameterInfo[] = [];
      const childRequired = resolved.required ?? [];
      const groupLabel =
        resolved[ExtensionProperties.Summary]
        ?? resolved.title
        ?? formatLabel(propName);
      const childGroup = { key: fullKey, name: propName, label: groupLabel };

      for (const [childName, childProp] of Object.entries(resolved.properties ?? {})) {
        const flattened = this.flattenBodyProperty(
          fullKey,
          childName,
          childProp,
          childRequired.includes(childName),
          childGroup,
        );
        parameters.push(...flattened);
      }

      return parameters;
    }

    // Leaf: produce a single ParameterInfo. Build it via the existing helper using
    // the leaf name (so label/summary/visibility/dynamic extensions read correctly),
    // then promote keys to the dotted full path so the body serializer
    // (`serializeBodyParameters` in swaggerParameterUtils) reconstructs the nested
    // structure on save.
    const leaf = this.propertyToParameterInfo(fullKey, propName, resolved, required);
    if (!leaf) {
      return [];
    }
    if (parentKey) {
      leaf.id = `param_${fullKey}`;
      leaf.parameterKey = fullKey;
      leaf.parameterName = fullKey;
    }
    if (parentGroup && leaf.info && leaf.info.parentProperty === undefined) {
      leaf.info.parentProperty = parentGroup;
    }
    return [leaf];
  }

  /**
   * Resolve a schema that might be a $ref.
   */
  private resolveSchema(schema: SwaggerParameter['schema']): SwaggerDefinition | null {
    if (!schema) return null;

    if ('$ref' in schema && typeof schema.$ref === 'string') {
      return this.resolveRef(schema.$ref);
    }

    return schema as SwaggerDefinition;
  }

  /**
   * Resolve a $ref to its definition.
   */
  private resolveRef(ref: string): SwaggerDefinition | null {
    const match = ref.match(/#\/definitions\/(.+)/);
    if (!match?.[1] || !this.api.definitions) {
      return null;
    }
    const defName = match[1];
    return this.api.definitions[defName] ?? null;
  }

  /**
   * Convert a swagger parameter to ParameterInfo.
   */
  private convertToParameterInfo(param: SwaggerParameter): ParameterInfo {
    const paramName = param.name ?? 'unknown';
    // Body parameters carry their type/schema under `param.schema` in Swagger 2.0,
    // not on `param` itself. Without this fallback an `in: body` parameter with
    // `schema: { type: 'object', additionalProperties: {...} }` would resolve to
    // ParameterType.Any and miss the Dictionary editor branch in `inferEditor`,
    // surfacing a plain string editor for what should be a key/value map.
    const schemaType = (param.in === 'body' ? param.schema?.type : undefined) ?? param.type;
    const paramType = swaggerTypeToParameterType(schemaType);
    const visibility = swaggerVisibilityToParameterVisibility(param[ExtensionProperties.Visibility]);
    const editor = inferEditor(param, paramType);

    const schema: SchemaObject = {
      type: schemaType ?? 'string',
    };

    if (param.description) {
      schema.description = param.description;
    }
    if (param.enum) {
      schema.enum = param.enum;
    }
    if (param.format) {
      schema.format = param.format;
    }

    // Build info object with dynamic values if present
    const paramInfo: ParameterInfo['info'] = {
      in: param.in,
    };

    // Extract URL encoding level from x-ms-url-encoding extension
    // This determines how path parameters are encoded: 'single', 'double', or 'none'
    const urlEncoding = param[ExtensionProperties.UrlEncoding] as string | undefined;
    if (urlEncoding) {
      paramInfo.encode = urlEncoding;
    }

    // Extract dynamic values configuration
    const dynamicValues = extractDynamicValues(param);
    if (dynamicValues) {
      paramInfo.dynamicValues = dynamicValues;
      paramInfo.isDynamic = true;
    }

    // Only x-ms-input-dependencies drives visibility; x-ms-dynamic-values `parameterReference`
    // must not hide the field (DynamicComboboxField shows its own waiting-state hint inline).
    const inputDependencies = extractInputDependencies(param);
    if (inputDependencies.length > 0) {
      paramInfo.inputDependencies = inputDependencies;
    }

    // Extract dynamic schema configuration
    // Check param → param.schema → resolved $ref target. SharePoint's
    // PostItem/PatchItem body uses `schema: { $ref: '#/definitions/Item' }`
    // and declares `x-ms-dynamic-schema` on the Item definition itself.
    let dynamicSchemaConfig = extractDynamicSchema(param);
    if (!dynamicSchemaConfig && param.schema) {
      // For body parameters, x-ms-dynamic-schema may be on the schema object
      const schemaObj = param.schema as Record<string, unknown>;
      if (schemaObj[ExtensionProperties.DynamicSchema]) {
        dynamicSchemaConfig = parseDynamicSchemaExtension(
          schemaObj[ExtensionProperties.DynamicSchema] as Parameters<typeof parseDynamicSchemaExtension>[0]
        );
      }
    }
    if (!dynamicSchemaConfig && param.schema) {
      const resolved = this.resolveSchema(param.schema) as Record<string, unknown> | null;
      const resolvedExtension = resolved?.[ExtensionProperties.DynamicSchema];
      if (resolvedExtension) {
        dynamicSchemaConfig = parseDynamicSchemaExtension(
          resolvedExtension as Parameters<typeof parseDynamicSchemaExtension>[0]
        );
      }
    }
    if (dynamicSchemaConfig) {
      paramInfo.dynamicSchema = dynamicSchemaConfig;
    }

    // Extract dynamic tree configuration for file/folder pickers
    const dynamicTreeConfig = extractDynamicTree(param);
    if (dynamicTreeConfig) {
      paramInfo.dynamicTree = dynamicTreeConfig;
    }

    // Extract serialization metadata (x-ms-serialization)
    const serializationConfig = extractSerializationMetadata(param);
    if (serializationConfig) {
      paramInfo.serialization = serializationConfig;
    }

    // Determine final editor: dynamic tree -> x-ms-editor -> inferred
    let finalEditor = editor;
    let editorOptions: Record<string, unknown> | undefined;

    if (dynamicTreeConfig) {
      // Dynamic tree takes precedence - use FilePicker
      finalEditor = ParameterEditor.FilePicker;
    } else {
      // Check for x-ms-editor override
      const editorOverride = extractEditorOverride(param);
      if (editorOverride) {
        finalEditor = editorOverride.editor;
        editorOptions = editorOverride.editorOptions;
      }
    }

    const info: ParameterInfo = {
      id: `param_${paramName}`,
      parameterKey: paramName,
      parameterName: paramName,
      label: param[ExtensionProperties.Summary] ?? formatLabel(paramName),
      type: paramType,
      required: param.required ?? false,
      editor: finalEditor,
      visibility,
      schema,
      value: [],
      info: paramInfo,
      dynamicData: {
        status: DynamicLoadStatus.NotStarted,
      },
      showTokens: true,
      showErrors: true,
      ...(editorOptions ? { editorOptions } : {}),
    };

    // Mirrors LogicAppsUX `toParameterInfoMap` filter (`!inputParameter.dynamicSchema`):
    // the parent placeholder for x-ms-dynamic-schema is never rendered or serialized.
    // The resolved leaf children produced by useDynamicSchema carry the real keys.
    if (dynamicSchemaConfig) {
      info.hideInUI = true;
      // Body root: children serialize directly under `body` (no path prefix).
      if (param.in === 'body') {
        info.info.bodyKeyPath = '';
      }
    }

    if (param.default !== undefined) {
      info.placeholder = String(param.default);
      info.defaultValue = param.default;
    }

    // Hide internal params kept solely by the default-value carve-out in
    // getInputParameters. When callers opt into internal params via
    // `excludeInternalParameters: false` and the param has no default,
    // we leave it visible so the caller's filter decides.
    if (
      param[ExtensionProperties.Visibility] === Visibility.Internal &&
      param.default !== undefined
    ) {
      info.hideInUI = true;
    }

    return info;
  }

  /**
   * Convert a swagger property to ParameterInfo.
   * Preserves nested properties for objects to enable token generation.
   */
  private propertyToParameterInfo(
    propertyKey: string,
    name: string,
    prop: SwaggerProperty,
    required: boolean
  ): ParameterInfo | null {
    // Carve-outs let internal props through when they (a) hold the webhook
    // callback URL or (b) declare a runtime default. Both are flagged
    // hideInUI below so the editor still skips them.
    const isInternal = prop[ExtensionProperties.Visibility] === Visibility.Internal;
    const isNotificationUrlProp = !!prop[ExtensionProperties.NotificationUrl];
    const propHasDefault = prop.default !== undefined;
    if (isInternal && !isNotificationUrlProp && !propHasDefault) {
      return null;
    }

    const paramType = swaggerTypeToParameterType(prop.type);
    const visibility = swaggerVisibilityToParameterVisibility(prop[ExtensionProperties.Visibility]);
    const editor = inferEditorFromProperty(prop, paramType);

    // Build schema preserving nested properties and array items
    const schema: SchemaObject = {
      type: prop.type ?? 'string',
    };

    if (prop.title) {
      schema.title = prop.title;
    }
    if (prop.description) {
      schema.description = prop.description;
    }
    if (prop.enum) {
      schema.enum = prop.enum;
    }
    if (prop.format) {
      schema.format = prop.format;
    }
    // Preserve nested object properties for token generation
    if (prop.properties) {
      schema.properties = this.resolveNestedProperties(prop.properties);
    }
    if (prop.required) {
      schema.required = prop.required;
    }
    // Preserve array item schema
    if (prop.items) {
      schema.items = this.resolveSchemaDeep(prop.items);
    }
    this.applySchemaExtensions(prop, schema);

    // Build info object with dynamic values if present
    const paramInfo: ParameterInfo['info'] = {
      in: 'body',
    };

    // Extract dynamic values configuration
    const dynamicValues = extractDynamicValues(prop);
    if (dynamicValues) {
      paramInfo.dynamicValues = dynamicValues;
      paramInfo.isDynamic = true;
    }

    // Only x-ms-input-dependencies drives visibility; x-ms-dynamic-values `parameterReference`
    // must not hide the field (DynamicComboboxField shows its own waiting-state hint inline).
    const inputDependencies = extractInputDependencies(prop);
    if (inputDependencies.length > 0) {
      paramInfo.inputDependencies = inputDependencies;
    }

    // Extract dynamic schema configuration (property-level)
    const dynamicSchemaConfig = extractDynamicSchemaFromProperty(prop);
    if (dynamicSchemaConfig) {
      paramInfo.dynamicSchema = dynamicSchemaConfig;
    }

    // Extract dynamic tree configuration for file/folder pickers
    const dynamicTreeConfig = extractDynamicTree(prop);
    if (dynamicTreeConfig) {
      paramInfo.dynamicTree = dynamicTreeConfig;
    }

    // Determine final editor: dynamic tree -> x-ms-editor -> inferred
    let finalEditor = editor;
    let editorOptions: Record<string, unknown> | undefined;

    if (dynamicTreeConfig) {
      // Dynamic tree takes precedence - use FilePicker
      finalEditor = ParameterEditor.FilePicker;
    } else {
      // Check for x-ms-editor override
      const editorOverride = extractEditorOverride(prop);
      if (editorOverride) {
        finalEditor = editorOverride.editor;
        editorOptions = editorOverride.editorOptions;
      }
    }

    const info: ParameterInfo = {
      id: `param_${name}`,
      parameterKey: propertyKey,
      parameterName: name,
      label: prop[ExtensionProperties.Summary] ?? prop.title ?? formatLabel(name),
      type: paramType,
      required,
      editor: finalEditor,
      visibility,
      schema,
      value: [],
      info: paramInfo,
      dynamicData: {
        status: DynamicLoadStatus.NotStarted,
      },
      showTokens: true,
      showErrors: true,
      ...(editorOptions ? { editorOptions } : {}),
    };

    if (prop.default !== undefined) {
      info.placeholder = String(prop.default);
      info.defaultValue = prop.default;
    }

    // Auto-fill the webhook callback URL and hide it from the UI.
    if (isNotificationUrlProp) {
      info.hideInUI = true;
      info.defaultValue = '${listCallbackUrl()}';
    } else if (isInternal) {
      // Internal-with-default carve-out (see early return): hide from the
      // editor but keep the default for the body serializer.
      info.hideInUI = true;
    }

    // Property-level dynamic schema: the parent property is a body sub-path
    // (e.g. SQL ExecuteProcedure_V2's `parameters`). Hide it from the UI and
    // record the body sub-path so resolved children serialize under
    // `body.<path>.<name>`. Mirrors LogicAppsUX's `keyPrefix` semantics.
    if (dynamicSchemaConfig) {
      info.hideInUI = true;
      info.info.bodyKeyPath = propertyKey;
    }

    return info;
  }

  /**
   * Resolve nested properties, handling $ref pointers.
   */
  private resolveNestedProperties(
    properties: Record<string, SwaggerProperty>
  ): Record<string, SchemaObject> {
    const resolved: Record<string, SchemaObject> = {};
    for (const [key, prop] of Object.entries(properties)) {
      resolved[key] = this.resolveSchemaDeep(prop);
    }
    return resolved;
  }

  /**
   * Copy the LogicAppsUX schema extensions (x-ms-visibility, x-ms-summary,
   * x-ms-dynamic-schema) and structural fields (additionalProperties) from a
   * swagger property onto the resolved schema. Shared between the top-level
   * (`propertyToParameterInfo`) and recursive (`resolveSchemaDeep`) paths so
   * both stay in sync. When `x-ms-summary` is present it also doubles as the
   * title fallback so legacy consumers that only read `title` still surface
   * the human label (e.g. "CC" instead of "ccRecipients"). Preserving
   * `x-ms-dynamic-schema` and `additionalProperties` is required for response
   * body schemas (e.g. SQL ExecuteProcedure_V2) so downstream code can later
   * resolve the real output shape and generate proper output tokens — without
   * this the manifest body would lose the dynamic-schema marker entirely.
   */
  private applySchemaExtensions(prop: SwaggerProperty, schema: SchemaObject): void {
    const visibility = prop[ExtensionProperties.Visibility];
    if (visibility === 'advanced' || visibility === 'internal' || visibility === 'important') {
      schema['x-ms-visibility'] = visibility;
    }
    const summary = prop[ExtensionProperties.Summary];
    if (typeof summary === 'string' && summary.length > 0) {
      schema['x-ms-summary'] = summary;
      if (!schema.title) schema.title = summary;
    }
    const dynamicSchema = (prop as Record<string, unknown>)[ExtensionProperties.DynamicSchema];
    if (dynamicSchema !== undefined) {
      schema['x-ms-dynamic-schema'] = dynamicSchema;
    }
    const additionalProperties = (prop as Record<string, unknown>)['additionalProperties'];
    if (additionalProperties !== undefined) {
      schema.additionalProperties = additionalProperties as boolean | SchemaObject;
    }
  }

  /**
   * Recursively resolve a schema, handling $ref pointers and preserving nested structure.
   */
  private resolveSchemaDeep(schema: SwaggerProperty | { $ref: string }): SchemaObject {
    // Handle $ref
    if ('$ref' in schema && typeof schema.$ref === 'string') {
      const resolved = this.resolveRef(schema.$ref);
      if (resolved) {
        return this.resolveSchemaDeep(resolved as SwaggerProperty);
      }
      return { type: 'object' };
    }

    const prop = schema as SwaggerProperty;
    const result: SchemaObject = {
      type: prop.type ?? 'object',
    };

    if (prop.title) result.title = prop.title;
    if (prop.description) result.description = prop.description;
    if (prop.format) result.format = prop.format;
    if (prop.enum) result.enum = prop.enum;
    if (prop.required) result.required = prop.required;
    this.applySchemaExtensions(prop, result);

    // Recursively resolve nested properties
    if (prop.properties) {
      result.properties = this.resolveNestedProperties(prop.properties);
    }

    // Recursively resolve array items
    if (prop.items) {
      result.items = this.resolveSchemaDeep(prop.items);
    }

    return result;
  }
}

// =============================================================================
// DEREFERENCE UTILITY
// =============================================================================

/**
 * Dereferences local $ref pointers in a Swagger document.
 * Only handles local JSON Pointer references (e.g., "#/definitions/MyType").
 * Cyclical references are detected and kept as $ref pointers.
 */
function dereferenceSwagger(swagger: SwaggerSpec): DereferencedSwagger {
  const resolvingRefs = new Set<string>();
  const cyclicalRefs: Record<string, CyclicalRefMetadata> = {};

  function decodeJsonPointer(segment: string): string {
    return segment.replace(/~1/g, '/').replace(/~0/g, '~');
  }

  function resolveRef(refPath: string): unknown {
    const parts = refPath.replace(/^#\//, '').split('/').map(decodeJsonPointer);
    let current: unknown = swagger;

    for (const part of parts) {
      if (current === null || typeof current !== 'object') {
        return undefined;
      }
      current = (current as Record<string, unknown>)[part];
    }
    return current;
  }

  function processValue(value: unknown): unknown {
    if (value === null || typeof value !== 'object') {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map((item) => processValue(item));
    }

    const obj = value as Record<string, unknown>;

    // Handle $ref
    if (typeof obj['$ref'] === 'string' && obj['$ref'].startsWith('#/')) {
      const refPath = obj['$ref'];

      // Detect cycle
      if (resolvingRefs.has(refPath)) {
        const resolved = resolveRef(refPath) as Record<string, unknown> | undefined;
        const refType = (resolved?.['type'] as string) || 'object';
        cyclicalRefs[refPath] = { type: refType };
        return { $ref: refPath };
      }

      resolvingRefs.add(refPath);
      const resolved = resolveRef(refPath);

      if (resolved === undefined) {
        resolvingRefs.delete(refPath);
        return { $ref: refPath };
      }

      const processed = processValue(resolved);
      resolvingRefs.delete(refPath);

      return processed;
    }

    // Process all properties
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(obj)) {
      result[key] = processValue(val);
    }
    return result;
  }

  const dereferenced = processValue(swagger) as DereferencedSwagger;

  if (Object.keys(cyclicalRefs).length > 0) {
    dereferenced.$refs = cyclicalRefs;
  }

  return dereferenced;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a parameter is the reserved connectionId parameter.
 */
function isReservedParameter(param: SwaggerParameter): boolean {
  if (!param.name) return false;
  return (
    param.name.toLowerCase() === ReservedParameterNames.ConnectionId.toLowerCase() &&
    param.required === true &&
    param.in === 'path' &&
    param[ExtensionProperties.Visibility] === Visibility.Internal &&
    param.type === 'string'
  );
}

/**
 * Sort swagger parameters by importance: required → important → default → advanced
 */
function sortParametersByImportance(parameters: SwaggerParameter[]): SwaggerParameter[] {
  const required: SwaggerParameter[] = [];
  const important: SwaggerParameter[] = [];
  const defaultVis: SwaggerParameter[] = [];
  const advanced: SwaggerParameter[] = [];

  for (const param of parameters) {
    const visibility = param[ExtensionProperties.Visibility];

    if (param.required) {
      required.push(param);
    } else if (visibility === Visibility.Important) {
      important.push(param);
    } else if (visibility === Visibility.Advanced) {
      advanced.push(param);
    } else {
      defaultVis.push(param);
    }
  }

  return [...required, ...important, ...defaultVis, ...advanced];
}

/**
 * Sort ParameterInfo array by importance: required → important → default → advanced
 */
function sortParameterInfoByImportance(parameters: ParameterInfo[]): ParameterInfo[] {
  const required: ParameterInfo[] = [];
  const important: ParameterInfo[] = [];
  const defaultVis: ParameterInfo[] = [];
  const advanced: ParameterInfo[] = [];

  for (const param of parameters) {
    if (param.required) {
      required.push(param);
    } else if (param.visibility === ParameterVisibility.Important) {
      important.push(param);
    } else if (param.visibility === ParameterVisibility.Advanced) {
      advanced.push(param);
    } else {
      defaultVis.push(param);
    }
  }

  return [...required, ...important, ...defaultVis, ...advanced];
}

/**
 * Convert swagger type to ParameterType.
 */
function swaggerTypeToParameterType(swaggerType?: string): ParameterType {
  switch (swaggerType) {
    case 'string':
      return ParameterType.String;
    case 'integer':
      return ParameterType.Integer;
    case 'number':
      return ParameterType.Number;
    case 'boolean':
      return ParameterType.Boolean;
    case 'array':
      return ParameterType.Array;
    case 'object':
      return ParameterType.Object;
    case 'file':
      return ParameterType.String; // Files are typically base64 strings
    default:
      return ParameterType.Any;
  }
}

/**
 * Convert swagger visibility to ParameterVisibility.
 */
function swaggerVisibilityToParameterVisibility(
  visibility?: SwaggerVisibility
): ParameterVisibility {
  switch (visibility) {
    case 'internal':
      return ParameterVisibility.Internal;
    case 'advanced':
      return ParameterVisibility.Advanced;
    case 'important':
      return ParameterVisibility.Important;
    default:
      return ParameterVisibility.Default;
  }
}

/**
 * Infer the best editor for a swagger parameter.
 */
function inferEditor(param: SwaggerParameter, paramType: ParameterType): ParameterEditor {
  // Dynamic values -> dynamic combobox
  if (hasDynamicValues(param)) {
    return ParameterEditor.DynamicCombobox;
  }

  // Static enum -> regular combobox
  if (param.enum && param.enum.length > 0) {
    return ParameterEditor.Combobox;
  }

  if (param.format === 'date-time') {
    return ParameterEditor.DateTime;
  }

  switch (paramType) {
    case ParameterType.Boolean:
      return ParameterEditor.Boolean;
    case ParameterType.Array:
      return ParameterEditor.Array;
    case ParameterType.Object:
      // Body parameters carry their schema under `param.schema`; non-body
      // parameters can still set `additionalProperties` directly on the param
      // (some swaggers do this when describing inline object types).
      return inferObjectEditor(
        (param.schema as { additionalProperties?: unknown } | undefined)
          ?? (param as unknown as { additionalProperties?: unknown }),
      );
    default:
      return ParameterEditor.String;
  }
}

/**
 * Infer the best editor for a swagger property.
 */
function inferEditorFromProperty(prop: SwaggerProperty, paramType: ParameterType): ParameterEditor {
  // Dynamic values -> dynamic combobox
  if (hasDynamicValues(prop)) {
    return ParameterEditor.DynamicCombobox;
  }

  // Static enum -> regular combobox
  if (prop.enum && prop.enum.length > 0) {
    return ParameterEditor.Combobox;
  }

  if (prop.format === 'date-time') {
    return ParameterEditor.DateTime;
  }

  switch (paramType) {
    case ParameterType.Boolean:
      return ParameterEditor.Boolean;
    case ParameterType.Array:
      return ParameterEditor.Array;
    case ParameterType.Object:
      return inferObjectEditor(prop);
    default:
      return ParameterEditor.String;
  }
}

/**
 * Pick the editor for an `object`-typed schema.
 *
 * Only true open-ended maps get the Dictionary editor — i.e. schemas that
 * explicitly opt into arbitrary key/value pairs via `additionalProperties:
 * true | { ... }`. Plain `{ type: 'object' }` (no `properties`, no
 * `additionalProperties`) means "any value" (often binary content, free-form
 * JSON, or an expression reference) and would be misleading as a key/value
 * grid — fall back to the tokenized String editor instead, matching how
 * `body` (no type) already behaves.
 *
 * Schemas with `properties` are flattened upstream (`processBodyParameter` /
 * `parseSchemaToParameters`) and don't reach this function via the inferred
 * leaf path.
 */
function inferObjectEditor(schema: { additionalProperties?: unknown }): ParameterEditor {
  const additional = schema.additionalProperties;
  const isOpenEndedMap =
    additional === true ||
    (typeof additional === 'object' && additional !== null);
  return isOpenEndedMap ? ParameterEditor.Dictionary : ParameterEditor.String;
}

/**
 * Format a parameter name as a display label.
 */
function formatLabel(name: string | undefined): string {
  if (!name) return '';
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

// =============================================================================
// DYNAMIC VALUES EXTRACTION
// =============================================================================

import type {
  SwaggerDynamicList,
  SwaggerDynamicValues,
  SwaggerDynamicTree,
  SwaggerDynamicTreeOperation,
  SwaggerEditor,
  SwaggerEditorOptions,
} from '@/types/swagger';

/**
 * Extract dynamic values configuration from swagger extensions.
 * Supports both x-ms-dynamic-list (modern) and x-ms-dynamic-values (legacy) formats.
 */
function extractDynamicValues(
  obj: SwaggerParameter | SwaggerProperty
): DynamicValuesConfig | undefined {

  // Check for modern x-ms-dynamic-list first
  const dynamicList = obj['x-ms-dynamic-list'] as SwaggerDynamicList | undefined;
  if (dynamicList?.operationId) {

    const config: DynamicValuesConfig = {
      operationId: dynamicList.operationId,
      isLegacy: false, // Modern x-ms-dynamic-list uses /dynamicList endpoint
    };

    // Convert parameters
    if (dynamicList.parameters) {
      const params: Record<string, DynamicValueParameter> = {};
      for (const [key, param] of Object.entries(dynamicList.parameters)) {
        const paramConfig: DynamicValueParameter = {};
        if (param.parameterReference !== undefined) {
          paramConfig.parameterReference = param.parameterReference;
        }
        if (param.value !== undefined) {
          paramConfig.value = param.value;
        }
        if (param.required !== undefined) {
          paramConfig.required = param.required;
        }
        params[key] = paramConfig;
      }
      config.parameters = params;
    }

    if (dynamicList.itemsPath) config.itemsPath = dynamicList.itemsPath;
    if (dynamicList.itemValuePath) config.itemValuePath = dynamicList.itemValuePath;
    if (dynamicList.itemTitlePath) config.itemTitlePath = dynamicList.itemTitlePath;
    if (dynamicList.itemDescriptionPath) config.itemDescriptionPath = dynamicList.itemDescriptionPath;
    if (dynamicList.itemSelectablePath) config.itemSelectablePath = dynamicList.itemSelectablePath;

    return config;
  }

  // Fall back to legacy x-ms-dynamic-values
  const dynamicValues = obj[ExtensionProperties.DynamicValues] as SwaggerDynamicValues | undefined;
  if (dynamicValues?.operationId) {

    const config: DynamicValuesConfig = {
      operationId: dynamicValues.operationId,
      isLegacy: true, // Legacy x-ms-dynamic-values uses /dynamicInvoke endpoint
    };

    // Convert legacy parameter format
    // In x-ms-dynamic-values:
    //   - Plain string like "ParentMessage" = static value
    //   - Object with { parameter: "foo" } = reference to parameter "foo"
    if (dynamicValues.parameters) {
      const params: Record<string, DynamicValueParameter> = {};
      for (const [key, paramValue] of Object.entries(dynamicValues.parameters)) {
        if (typeof paramValue === 'string') {
          // Plain string = static value (NOT a parameter reference)
          params[key] = { value: paramValue };
        } else if (paramValue && typeof paramValue === 'object') {
          // Object format with parameter/value - cast to access properties
          const paramObj = paramValue as { parameter?: string; value?: unknown };
          const paramConfig: DynamicValueParameter = {};
          if (paramObj.parameter !== undefined) {
            paramConfig.parameterReference = paramObj.parameter;
          }
          if (paramObj.value !== undefined) {
            paramConfig.value = paramObj.value;
          }
          params[key] = paramConfig;
        }
      }
      config.parameters = params;
    }

    // Map legacy property names
    if (dynamicValues['value-collection']) config.itemsPath = dynamicValues['value-collection'];
    if (dynamicValues['value-path']) config.itemValuePath = dynamicValues['value-path'];
    if (dynamicValues['value-title']) config.itemTitlePath = dynamicValues['value-title'];
    return config;
  }

  return undefined;
}

/**
 * Check if a swagger parameter/property has fetchable dynamic values.
 * Returns true only if there's an operationId we can call.
 * Built-in operations (builtInOperation) are not fetchable via connector APIs.
 */
function hasDynamicValues(obj: SwaggerParameter | SwaggerProperty): boolean {
  // Check x-ms-dynamic-list
  const dynamicList = obj[ExtensionProperties.DynamicList] as { operationId?: string } | undefined;
  if (dynamicList?.operationId) {
    return true;
  }

  // Check x-ms-dynamic-values - must have operationId (not builtInOperation)
  const dynamicValues = obj[ExtensionProperties.DynamicValues] as { operationId?: string; builtInOperation?: string } | undefined;
  if (dynamicValues?.operationId) {
    return true;
  }

  // builtInOperation is not fetchable - treat as regular field
  return false;
}

// =============================================================================
// INPUT DEPENDENCIES EXTRACTION (x-ms-input-dependencies)
// =============================================================================

/**
 * Swagger input dependency configuration from x-ms-input-dependencies extension
 *
 * Format in swagger:
 * "x-ms-input-dependencies": {
 *   "parameterReference": {
 *     "type": "visibility",
 *     "values": ["value1", "value2"],
 *     "excludeValues": ["value3"]
 *   }
 * }
 */
interface SwaggerInputDependencyConfig {
  type?: 'visibility' | 'value';
  values?: unknown[];
  excludeValues?: unknown[];
}

/**
 * Extract input dependencies from x-ms-input-dependencies extension.
 * These are used for conditional parameter visibility - show/hide parameters
 * based on the values of other parameters.
 *
 * @param obj - Swagger parameter or property that may have x-ms-input-dependencies
 * @returns Array of InputDependency objects for visibility evaluation
 */
function extractInputDependencies(
  obj: SwaggerParameter | SwaggerProperty
): InputDependency[] {
  const inputDeps = obj[ExtensionProperties.InputDependencies] as
    | Record<string, SwaggerInputDependencyConfig>
    | undefined;

  if (!inputDeps || typeof inputDeps !== 'object') {
    return [];
  }

  const dependencies: InputDependency[] = [];

  for (const [parameterReference, config] of Object.entries(inputDeps)) {
    // Skip invalid entries
    if (!config || typeof config !== 'object') {
      continue;
    }

    const dependency: InputDependency = {
      type: config.type === 'value' ? InputDependencyType.Value : InputDependencyType.Visibility,
      parameterReference,
    };

    // Add values array if present (show when param equals any of these)
    if (Array.isArray(config.values) && config.values.length > 0) {
      dependency.values = config.values;
    }

    // Add excludeValues array if present (hide when param equals any of these)
    if (Array.isArray(config.excludeValues) && config.excludeValues.length > 0) {
      dependency.excludeValues = config.excludeValues;
    }

    // Only add if we have actual conditions to evaluate
    if (dependency.values || dependency.excludeValues) {
      dependencies.push(dependency);
    }
  }

  return dependencies;
}

// =============================================================================
// DYNAMIC SCHEMA EXTRACTION (x-ms-dynamic-schema)
// =============================================================================

/**
 * Extract dynamic schema configuration from a swagger parameter.
 *
 * Dynamic schema allows parameters to generate additional input fields
 * based on the schema returned from an API call.
 *
 * Format in swagger:
 * "x-ms-dynamic-schema": {
 *   "operationId": "GetTableSchema",
 *   "parameters": {
 *     "dataset": { "parameter": "dataset" },
 *     "table": { "parameter": "table" }
 *   },
 *   "value-path": "schema"
 * }
 */
function extractDynamicSchema(
  param: SwaggerParameter
): DynamicSchemaConfig | undefined {
  const dynamicSchema = param[ExtensionProperties.DynamicSchema];
  if (!dynamicSchema?.operationId) {
    return undefined;
  }

  return parseDynamicSchemaExtension(dynamicSchema);
}

/**
 * Extract dynamic schema configuration from a swagger property.
 * Properties don't typically have x-ms-dynamic-schema, but some connectors use it.
 */
function extractDynamicSchemaFromProperty(
  prop: SwaggerProperty
): DynamicSchemaConfig | undefined {
  // Properties typically don't have dynamic schema, but check the raw object
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dynamicSchema = (prop as any)[ExtensionProperties.DynamicSchema];
  if (!dynamicSchema?.operationId) {
    return undefined;
  }

  return parseDynamicSchemaExtension(dynamicSchema);
}

/**
 * Parse the x-ms-dynamic-schema extension into DynamicSchemaConfig
 */
function parseDynamicSchemaExtension(
  dynamicSchema: {
    operationId?: string;
    parameters?: Record<string, unknown>;
    'value-path'?: string;
  }
): DynamicSchemaConfig | undefined {
  if (!dynamicSchema.operationId) {
    return undefined;
  }

  const config: DynamicSchemaConfig = {
    operationId: dynamicSchema.operationId,
  };

  // Convert parameters
  if (dynamicSchema.parameters) {
    const params: Record<string, DynamicSchemaParameter> = {};

    for (const [key, paramValue] of Object.entries(dynamicSchema.parameters)) {
      if (paramValue && typeof paramValue === 'object') {
        const paramObj = paramValue as { parameter?: string; value?: unknown };
        const paramConfig: DynamicSchemaParameter = {};

        // Handle parameter reference (e.g., { "parameter": "dataset" })
        if (paramObj.parameter !== undefined) {
          paramConfig.parameterReference = paramObj.parameter;
        }
        // Handle static value
        if (paramObj.value !== undefined) {
          paramConfig.value = paramObj.value;
        }
        params[key] = paramConfig;
      } else if (typeof paramValue === 'string') {
        // Simple string = STATIC VALUE, not a parameter reference
        // e.g., "actionType": "Message" means value="Message", not reference to param "Message"
        params[key] = { value: paramValue };
      }
    }

    config.parameters = params;
  }

  // Extract value path for schema location in response
  if (dynamicSchema['value-path']) {
    config.valuePath = dynamicSchema['value-path'];
  }

  return config;
}

// =============================================================================
// DYNAMIC TREE EXTRACTION (x-ms-dynamic-tree)
// =============================================================================

/**
 * Extract dynamic tree configuration from a swagger parameter.
 *
 * Dynamic tree allows file/folder selection with tree navigation.
 * Used by SharePoint, OneDrive, Azure Blob connectors.
 *
 * Format in swagger:
 * "x-ms-dynamic-tree": {
 *   "open": {
 *     "operationId": "GetRootFolders",
 *     "parameters": { ... },
 *     "itemsPath": "value",
 *     "itemValuePath": "id",
 *     "itemTitlePath": "name",
 *     "itemIsParentPath": "isFolder"
 *   },
 *   "browse": {
 *     "operationId": "GetFolderContents",
 *     "parameters": {
 *       "folderId": { "selectedItemValuePath": "id" }
 *     },
 *     ...
 *   },
 *   "settings": {
 *     "canSelectParentNodes": true,
 *     "canSelectLeafNodes": true
 *   }
 * }
 */
function extractDynamicTree(
  obj: SwaggerParameter | SwaggerProperty
): DynamicTreeConfig | undefined {
  const dynamicTree = obj[ExtensionProperties.DynamicTree] as SwaggerDynamicTree | undefined;
  if (!dynamicTree) {
    return undefined;
  }

  // Must have at least open operation
  if (!dynamicTree.open?.operationId) {
    return undefined;
  }

  const config: DynamicTreeConfig = {
    open: parseDynamicTreeOperation(dynamicTree.open),
    browse: parseDynamicTreeOperation(dynamicTree.browse ?? dynamicTree.open),
  };

  // Parse settings if present
  if (dynamicTree.settings) {
    const settings: DynamicTreeSettings = {};
    const s = dynamicTree.settings;

    if (s.collectionPath !== undefined) settings.collectionPath = s.collectionPath;
    if (s.valuePath !== undefined) settings.valuePath = s.valuePath;
    if (s.titlePath !== undefined) settings.titlePath = s.titlePath;
    if (s.folderPropertyName !== undefined) settings.folderPropertyName = s.folderPropertyName;
    if (s.mediaPropertyName !== undefined) settings.mediaPropertyName = s.mediaPropertyName;
    if (s.canSelectParentNodes !== undefined) settings.canSelectParentNodes = s.canSelectParentNodes;
    if (s.canSelectLeafNodes !== undefined) settings.canSelectLeafNodes = s.canSelectLeafNodes;

    config.settings = settings;
  }

  return config;
}

/**
 * Parse a dynamic tree operation (open or browse) into DynamicTreePickerInfo
 */
function parseDynamicTreeOperation(
  op: SwaggerDynamicTreeOperation
): DynamicTreePickerInfo {
  const info: DynamicTreePickerInfo = {
    operationId: op.operationId ?? '',
  };

  // Parse parameters
  if (op.parameters) {
    const params: Record<string, DynamicTreeParameter> = {};

    for (const [key, param] of Object.entries(op.parameters)) {
      const paramConfig: DynamicTreeParameter = {};

      if (param.parameterReference !== undefined) {
        paramConfig.parameterReference = param.parameterReference;
      }
      if (param.selectedItemValuePath !== undefined) {
        paramConfig.selectedItemValuePath = param.selectedItemValuePath;
      }
      if (param.value !== undefined) {
        paramConfig.value = param.value;
      }

      params[key] = paramConfig;
    }

    info.parameters = params;
  }

  // Copy path configurations
  if (op.itemsPath) info.itemsPath = op.itemsPath;
  if (op.itemValuePath) info.itemValuePath = op.itemValuePath;
  if (op.itemTitlePath) info.itemTitlePath = op.itemTitlePath;
  if (op.itemIsParentPath) info.itemIsParentPath = op.itemIsParentPath;
  // Office 365 / SharePoint encode the folder check as an OData expression
  // like "(IsFolder eq true)" under the `itemIsParent` key. Extract the
  // property name so downstream code can resolve it the same way as
  // `itemIsParentPath`. We're conservative: only handle the simple
  // `<prop> eq true` form (with optional surrounding parens / whitespace).
  if (!info.itemIsParentPath && op.itemIsParent) {
    const match = op.itemIsParent.match(/\(?\s*([A-Za-z_][\w.]*)\s+eq\s+true\s*\)?/i);
    if (match?.[1]) {
      info.itemIsParentPath = match[1];
    }
  }
  if (op.itemFullTitlePath) info.itemFullTitlePath = op.itemFullTitlePath;
  if (op.selectableFilter) info.selectableFilter = op.selectableFilter;

  return info;
}

// =============================================================================
// EDITOR EXTENSION EXTRACTION (x-ms-editor)
// =============================================================================

/**
 * Extract editor type override from x-ms-editor extension.
 *
 * Supports:
 * - 'html': Rich text HTML editor (Lexical-based)
 * - 'code': Code editor
 * - 'dictionary': Key-value pair editor
 * - 'array': Array editor
 * - 'authentication': Authentication config editor
 */
function extractEditorOverride(
  obj: SwaggerParameter | SwaggerProperty
): { editor: ParameterEditor; editorOptions?: Record<string, unknown> } | undefined {
  const editorType = obj[ExtensionProperties.Editor] as SwaggerEditor | undefined;
  if (!editorType) {
    return undefined;
  }

  let editor: ParameterEditor;
  switch (editorType) {
    case 'html':
      editor = ParameterEditor.Html;
      break;
    case 'code':
      editor = ParameterEditor.Code;
      break;
    case 'dictionary':
      editor = ParameterEditor.Dictionary;
      break;
    case 'array':
      editor = ParameterEditor.Array;
      break;
    case 'authentication':
      editor = ParameterEditor.Authentication;
      break;
    default:
      return undefined;
  }

  // Extract editor options if present
  const editorOptions = obj[ExtensionProperties.EditorOptions] as SwaggerEditorOptions | undefined;

  const result: { editor: ParameterEditor; editorOptions?: Record<string, unknown> } = { editor };
  if (editorOptions) {
    result.editorOptions = { ...editorOptions };
  }
  return result;
}

// =============================================================================
// SERIALIZATION METADATA EXTRACTION
// =============================================================================

/**
 * Extract serialization metadata from x-ms-serialization extension.
 *
 * This extension specifies how parameter values should be serialized/deserialized,
 * particularly for path template parameters where values need to be extracted
 * from or embedded in URL paths.
 *
 * Example swagger extension:
 * {
 *   "x-ms-serialization": {
 *     "property": {
 *       "type": "pathtemplate",
 *       "parameterReference": "inputs.operationDetails.pathParameters"
 *     }
 *   }
 * }
 *
 * @param obj - SwaggerParameter or SwaggerProperty to extract from
 * @returns ParameterSerializationOptions or undefined if not present
 */
function extractSerializationMetadata(
  obj: SwaggerParameter | SwaggerProperty
): ParameterSerializationOptions | undefined {
  const paramName = (obj as SwaggerParameter).name || 'unknown';

  // Get the x-ms-serialization extension
  const serialization = (obj as Record<string, unknown>)[
    ExtensionProperties.Serialization
  ] as SwaggerSerializationOptions | undefined;

  if (!serialization) {
    return undefined;
  }

  const result: ParameterSerializationOptions = {};

  // Handle skip flag
  if (serialization.skip !== undefined) {
    result.skip = serialization.skip;
  }

  // Handle property configuration
  if (serialization.property) {
    const property: SerializationProperty = {
      name: serialization.property.name || paramName,
      type: serialization.property.type || '',
    };

    // Include parameterReference if present (critical for path template routing)
    if (serialization.property.parameterReference) {
      property.parameterReference = serialization.property.parameterReference;
    }

    result.property = property;
  }
  return result;
}
